import React, { useState } from "react";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ProviderSetCountResult } from "./jobStatisticsResult";
import OperationsService from "./operationsService";
import { FlyoutLink } from "@app/shared/Flyouts";
import ProviderSetVersionsFlyoutContent from "./ProviderSetVersionsFlyoutContent"; 

const operationsService = new OperationsService();

const getMostRecentSaturday = (): string =>
  (d => (d.setDate(d.getDate() - ((d.getDay() + 1) % 7)), d.toISOString().split("T")[0]))(new Date());

interface ProviderSetVersionStatusProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const ProviderSetVersionStatus: React.FC<ProviderSetVersionStatusProps> = ({
  clients,
  clientId,
  setClientId,
}) => {
  const [providerSetData, setProviderSetData] = useState<ProviderSetCountResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [runDate, setRunDate] = useState<string>(getMostRecentSaturday());

  const fetchProviderSetCounts = async () => {
    if (clientId === null) {
      setError("Please select a client.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await operationsService.getProviderSetCounts(clientId, runDate);
      setProviderSetData(result); 
    } catch (err) {
      setError("Error fetching provider set version status");
    } finally {
      setLoading(false); 
    }
  };

  const providerSetColumns: ColumnConfig = {
    createDate: {
      displayText: "Create Date",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-create-date-output",
    },
    isActivated: {
      displayText: "Is Activated",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-is-activated-output",
    },
    providerSetCount: {
      displayText: "Provider Set Count",
      sizeContentToFit: true,
      testId: "weekend-run-monitoring__provider-set-count-output",
    },
  };

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col">
          <QESSelect
            label="Client"
            value={clientId !== null ? clientId.toString() : ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            testId="weekend-run-monitoring__provider-set-client-select"
            placeholder="Select Client"
            required
            isValid={clientId !== null}
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="weekend-run-monitoring__provider-set-run-date-input"
          />
        </div>
        <div className="col d-flex align-items-end">
          <QESButton
            onClick={fetchProviderSetCounts}
            qesType="primary"
            qesTestId="weekend-run-monitoring__fetch-provider-set-button"
          >
            Get Results
          </QESButton>
        </div>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div>{error}</div>}
      {providerSetData.length > 0 && (
        <Waffles
          columns={providerSetColumns}
          data={providerSetData.map((stat, index) => ({
            /* eslint-disable sort-keys */
            key: index.toString(),
            createDate: (
              <FlyoutLink
                classNames="p-0 qes-button-secondary"
                content={{
                  title: `Provider Set Versions for ${new Date(stat.createDate).toLocaleDateString()}`,
                  body: (
                    <ProviderSetVersionsFlyoutContent
                      clientId={clientId}
                      createDate={new Date(stat.createDate)}
                      isActivated={stat.isActivated}
                    />
                  ),
                }}
                testId={`weekend-run-monitoring__provider-set-flyout-${index}`}
                size="80"
              >
                {new Date(stat.createDate).toLocaleDateString()}
              </FlyoutLink>
            ),
            providerSetCount: stat.providerSetCount,
            isActivated: stat.isActivated ? "True" : "False",
            /* eslint-enable sort-keys */
          }))}
          enablePagination={false}
          passThroughSorting={true}
          testId="weekend-run-monitoring__provider-set-count-table"
        />
      )}
    </div>
  );
};

export default ProviderSetVersionStatus;